import { Children, Fragment, isValidElement, type ReactNode } from 'react'

/**
 * Does an actions cell have anything to show?
 *
 * Action cells are built as a list of conditionals — a pencil behind
 * `canManage`, a bin behind `canDelete && !row.isSystem`, an "Open" link behind
 * a foreign key that may be null. Each guard is right on its own, but together
 * they have a case nobody designs for: the row where every one of them is
 * false. The cell then renders as blank space under a populated "Actions"
 * header, which reads as a page that failed to load rather than as a row with
 * nothing to do — and because it depends on the row, it shows up on *some*
 * rows of a table whose other rows look fine.
 *
 * `RowActions` uses this to put the app's usual `—` there instead. Keeping the
 * predicate in a plain `.ts` module is what makes it testable: `vitest.config.ts`
 * is `environment: 'node'` with `include: ['src/**\/*.test.ts']`, so a `.tsx`
 * test would silently never run.
 *
 * What counts as renderable mirrors what React itself paints: `null`,
 * `undefined`, `false` and empty/whitespace-only strings are nothing — which is
 * exactly what a `{cond && <Button/>}` collapses to when `cond` is false.
 * Fragments are looked through, so grouping several conditionals in a `<>…</>`
 * does not read as content; any other element counts, without rendering it.
 */
export function hasRenderableActions(children: ReactNode): boolean {
  return Children.toArray(children).some(isRenderable)
}

function isRenderable(node: ReactNode): boolean {
  if (node == null || typeof node === 'boolean') return false
  if (typeof node === 'string') return node.trim().length > 0
  if (Array.isArray(node)) return node.some(isRenderable)
  if (isValidElement(node) && node.type === Fragment) {
    return hasRenderableActions((node.props as { children?: ReactNode }).children)
  }
  return true
}
